// ============================================================
// CDK Overlay fallback — TWS-122 Wave 3
// ------------------------------------------------------------
// Browsers without the native `[popover]` API + CSS anchor
// positioning (anything older than Chrome/Edge 125 / Safari 26.1
// / Firefox 149) need an alternate positioning path. This
// service wraps `@angular/cdk/overlay` so each primitive's
// fallback branch can reuse the same open/close/light-dismiss
// wiring.
//
// Intentional boundaries:
//
//   - No new CDK APIs — the service composes `Overlay`,
//     `OverlayPositionBuilder`, `FlexibleConnectedPositionStrategy`
//     exactly as those APIs expect.
//   - The native fast path does NOT touch this service. Modern
//     browsers stay on `[popover]` + CSS anchor positioning with
//     zero added JS cost; this service only instantiates when
//     `getPositioningStrategy() === 'cdk-fallback'`.
//   - Light-dismiss parity: native `[popover]` auto-closes on
//     outside click + Escape. CDK Overlay with a transparent
//     backdrop does not, so we wire those listeners ourselves.
//   - The primitives already own their content DOM (the `<div>`
//     carrying `[popover]`). The service moves that node into
//     the CDK overlay pane rather than owning a Portal, keeping
//     a single source of truth for the element and preserving
//     its existing BEM class / listeners.
// ============================================================

import {
  ConnectedPosition,
  Overlay,
  OverlayRef,
} from '@angular/cdk/overlay';
import { inject, Injectable } from '@angular/core';

import { TwPopoverPlacement } from './types';

/**
 * Options the primitives pass when opening a fallback overlay.
 */
export interface TwOverlayFallbackOptions {
  /**
   * Whether the overlay installs light-dismiss: outside click
   * and Escape both call the `onDismiss` callback. Dropdown and
   * popover use `true`; tooltip uses `false` (tooltips dismiss
   * via their own mouse/focus handlers).
   */
  readonly lightDismiss: boolean;
  /** Called on outside click or Escape. */
  readonly onDismiss?: () => void;
  /**
   * When `true`, the overlay pane does not intercept pointer
   * events — used by tooltip so the underlying page stays
   * interactive while the tooltip is visible.
   */
  readonly passthroughPointerEvents?: boolean;
}

/**
 * Handle the primitives hold onto so they can dispose their
 * overlay on close. Keeps the service API asymmetric (open /
 * close as separate calls) to match the way the primitives
 * already model `ctx.open` rising/falling edges.
 */
export interface TwOverlayFallbackHandle {
  /** The underlying CDK ref, exposed for edge cases (not commonly used). */
  readonly overlayRef: OverlayRef;
  /** Dispose the overlay and detach listeners. Idempotent. */
  dispose(): void;
}

/**
 * Map `TwPopoverPlacement` → a CDK `ConnectedPosition[]` with
 * sensible auto-flip fallbacks. Each preferred placement lists
 * its own CDK-position first, followed by the three mirror
 * variants so CDK can rotate when the preferred side overflows.
 *
 * Inline offset (8 px) mirrors the 8 px gap used in
 * `tw-popover-positioning.scss`. The two stay in lockstep —
 * change both or neither.
 *
 * @internal — exported for unit tests.
 */
export function placementToConnectedPositions(
  placement: TwPopoverPlacement,
): ConnectedPosition[] {
  const below: ConnectedPosition = {
    originX: 'start',
    originY: 'bottom',
    overlayX: 'start',
    overlayY: 'top',
    offsetY: 8,
  };
  const above: ConnectedPosition = {
    originX: 'start',
    originY: 'top',
    overlayX: 'start',
    overlayY: 'bottom',
    offsetY: -8,
  };
  const after: ConnectedPosition = {
    originX: 'end',
    originY: 'top',
    overlayX: 'start',
    overlayY: 'top',
    offsetX: 8,
  };
  const before: ConnectedPosition = {
    originX: 'start',
    originY: 'top',
    overlayX: 'end',
    overlayY: 'top',
    offsetX: -8,
  };

  // Each placement's preferred position is listed first; the
  // remainder provide auto-flip fallbacks on viewport overflow.
  switch (placement) {
    case 'top':
      return [above, below, after, before];
    case 'bottom':
      return [below, above, after, before];
    case 'start':
      return [before, after, above, below];
    case 'end':
      return [after, before, above, below];
  }
}

/**
 * Root-provided service that the primitives' CDK-fallback
 * branches call into.
 */
@Injectable({ providedIn: 'root' })
export class TwOverlayFallbackService {
  private readonly overlay = inject(Overlay);

  /**
   * Attach `contentEl` to the CDK overlay, anchored to
   * `triggerEl`. Returns a handle the caller disposes on close.
   *
   * The caller provides the content DOM — we do NOT create the
   * element. That matches the shape Wave 2 components already
   * use (they own the host `<div>` carrying `[popover]`) and
   * avoids a redundant PortalOutlet dance for template /
   * component portals when the element already exists.
   */
  openAt(
    triggerEl: HTMLElement,
    contentEl: HTMLElement,
    placement: TwPopoverPlacement,
    options: TwOverlayFallbackOptions,
  ): TwOverlayFallbackHandle {
    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(triggerEl)
      .withPositions(placementToConnectedPositions(placement))
      .withFlexibleDimensions(false)
      .withPush(false);

    const overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      // Transparent backdrop keeps the page visible while the
      // popover is up. `hasBackdrop: false` for tooltip so there
      // is nothing intercepting clicks at all.
      hasBackdrop: options.lightDismiss,
      backdropClass: 'cdk-overlay-transparent-backdrop',
      panelClass: options.passthroughPointerEvents
        ? 'tw-popover-fallback-passthrough'
        : undefined,
    });

    // Move the caller-supplied element into the CDK overlay
    // pane. The primitives own this element's attributes +
    // listeners; we just re-parent it so CDK's position strategy
    // can take over. Disposing the overlay tears the pane down
    // along with our appended child.
    overlayRef.overlayElement.appendChild(contentEl);

    // Light-dismiss — native `[popover="auto"]` gives outside-
    // click + Escape closure for free. CDK Overlay does not,
    // so wire both manually when requested.
    let escapeHandler: ((e: KeyboardEvent) => void) | null = null;
    if (options.lightDismiss) {
      overlayRef
        .backdropClick()
        .subscribe(() => options.onDismiss?.());
      escapeHandler = (e: KeyboardEvent): void => {
        if (e.key === 'Escape') options.onDismiss?.();
      };
      document.addEventListener('keydown', escapeHandler);
    }

    const dispose = (): void => {
      if (escapeHandler) {
        document.removeEventListener('keydown', escapeHandler);
        escapeHandler = null;
      }
      overlayRef.dispose();
    };

    return { overlayRef, dispose };
  }
}
